import { makeAutoObservable, reaction, runInAction } from "mobx";
import { v4 as uuid } from "uuid";

import {
  AttachedFile,
  ChatExperiments,
  IChannel,
  IContractPreviewItem,
  IFileAttachment,
  IMessage,
  ChatLetterOutput,
  ChatLetterSummary,
  DocumentOutputAvailableEvent,
  IPageRotation,
  IPolicyPreviewItem,
  IRenameFileTarget,
  RawMessage,
  StatusMessageState,
  STOP_RESPONDING_MESSAGE,
  VALID_ACTIONS,
  VALID_PHASES,
  WEBSOCKET_MESSAGE_TYPES,
} from "../types/chat";
import {
  apiService,
  chatService,
  ChatSession,
  websocketService,
} from "../services";
import { channelsConfig } from "../config/channels";
import { ChatHelper } from "../components/helpers/chatHelper";
import { WS_EVENTS } from "../types/wsEvents";
import { APP_CONFIG_FLAGS } from "../types/app";
import {
  DEFAULT_MODEL,
  fromModelVersion,
  toModelVersion,
} from "../config/chatModels";
import type MainStore from "./MainStore";
import { getPayerDisplayName, LOB_VALUE_TO_NAME } from "../config";
import type {
  ClaimAppeal,
  ClaimDetailResponse,
  ClaimsDashboardStatistics,
} from "../types/claims";
import { isEdiFile } from "../services/fileParser";
import type { ApiResponse } from "../services/apiService";
import { channelKey } from "../utils/channelKey";
import { isChannelClosed } from "../utils/chatClosed";
import { DEFAULT_SIDEBAR_FILTERS, ISidebarFilters } from "../types/sidebar";

/** How long a claim refresh waits after the last EDI upload frame. Long enough
 * to coalesce a multi-file drop, short enough that the panel still feels live. */
const CLAIM_REFRESH_DEBOUNCE_MS = 1500;
// Mirrors UNANSWERED_TURN_GRACE in app-backend chat_lifecycle (REV-2553).
const UNANSWERED_GRACE_MS = 30_000;
// Re-read a little after the window so server/client clock skew can't land early.
const UNANSWERED_RECHECK_SLACK_MS = 2_000;
// Reads per message before giving up: covers a read that lands just before the
// server flags the row, or that fails outright.
const UNANSWERED_RECHECK_MAX_ATTEMPTS = 3;

// Global, not per tenant and user like "filters:<page>": view preferences only (see filtersTenantStorage).
const SIDEBAR_FILTERS_STORAGE_KEY = "sidebarFilters";

/** Loads the sidebar "Filter chat history" panel choices saved by
 * setSidebarFilters, so they survive a page refresh or a later login.
 * Merged onto the defaults so a stale/partial saved value (e.g. from before
 * a new filter field was added) doesn't leave that field undefined. */
function loadSidebarFilters(): ISidebarFilters {
  try {
    const saved = localStorage.getItem(SIDEBAR_FILTERS_STORAGE_KEY);
    if (saved) {
      const { showClosedChats, ...parsed } = JSON.parse(saved);
      // Pre-rename saves stored the inverse flag as showClosedChats.
      if (typeof showClosedChats === "boolean" && parsed.hideClosedChats === undefined) {
        parsed.hideClosedChats = !showClosedChats;
      }
      return { ...DEFAULT_SIDEBAR_FILTERS, ...parsed };
    }
  } catch {
    // Ignore localStorage errors
  }
  return DEFAULT_SIDEBAR_FILTERS;
}

type PanelViewHistoryEntry =
  | { type: "file"; file: IFileAttachment & { chatId?: string } }
  | { type: "letter"; letter: ChatLetterOutput }
  | { type: "claim"; claimId: string };

export default class ChatStore {
  // Reference to MainStore (set by MainStore after creation) for this.mainStore.chatCategoryStore / this.mainStore.chatStore
  mainStore: MainStore | null = null;

  // Controlling variables
  activeChatId: string | null = null;
  channel: string | null = null;

  // Data
  chatDetails: IChannel | null = null;
  /** Why the last load finished without `chatDetails`: "not_found" only for
   * 403/404, "failed" otherwise, `null` if it succeeded or has not resolved. */
  chatLoadFailure: "not_found" | "failed" | null = null;
  /** Non-production switches for the active chat (REV-2161), e.g. { mock: true }.
   *
   * `null` means no choice is recorded yet — a chat that does not exist server
   * side. Loading a chat always sets a concrete value, so a stored `{}` (the
   * real agent) is never mistaken for "unset" and silently re-defaulted. */
  experiments: ChatExperiments | null = null;
  /** The model ModelSelector shows. Per chat, owned here rather than by pages:
   * - a loaded chat shows its own stored model (set in loadChatMessages, which
   *   starts from the default so a failed or in-flight load never shows the
   *   previous chat's model);
   * - leaving a chat for a draft (reset() with a chat active) goes back to the
   *   default, so a new chat never silently inherits the last chat's model;
   * - a pick made on a draft survives draft-to-draft resets (e.g. the reset
   *   Home runs right before askQuestion) and rides the create call. */
  selectedModel: string = DEFAULT_MODEL;
  /** A model change is being written to the active chat. */
  isSavingModel: boolean = false;
  /** The chat whose model the user explicitly changed in ModelSelector.
   * Cleared when a chat's stored model is (re)loaded. */
  modelChangedForChatId: string | null = null;
  messages: IMessage[] = [];
  statusMessages: StatusMessageState[] = [];
  uploadedFiles: AttachedFile[] = [];
  /** Every version of the active chat's generated documents (appeal letters),
   * without bodies, from loadChatLetters. */
  chatLetters: ChatLetterSummary[] = [];
  isChatLettersLoading: boolean = false;
  /** The active chat's most recent generated letter, with its body. The only
   * version that can be edited. */
  latestChatLetter: ChatLetterOutput | null = null;
  /** An editor change to the open letter not yet confirmed by the backend. */
  letterDraft: { chatId: string; generation: number; content: string } | null = null;
  /** Serialises letter saves, so an older one can never land after a newer one. */
  private letterSaveChain: Promise<boolean> = Promise.resolve(true);
  /** A document_output_available arrived this turn: the turn's textless final
   * is its terminator, not a message to render. */
  private letterTurnAwaitingFinal = false;

  // Polling (read-only guest viewer)
  lastPollTimestamp: string | null = null;

  // Loaders
  isChatLoading: boolean = true;
  isQuestioning: boolean = false;
  /** Text of a turn the backend refused, handed back to the composer. */
  rejectedInput: string | null = null;
  /** One-shot notice for the UI to surface, then clear. */
  notice: string | null = null;
  /** Last message inside the backend's unanswered grace window (REV-2553). */
  stillSendingMessageId: string | null = null;
  private unansweredRecheckTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bumped on cancel so a re-read already in flight doesn't re-arm itself. */
  private unansweredRecheckGeneration = 0;
  isAborting: boolean = false;

  // File Viewer
  fileToPreview: IFileAttachment | null = null;
  letterToPreview: ChatLetterOutput | null = null;
  claimIdToPreview: string | null = null;
  /** When ClaimPreview is closed, store EDI file name so correct tab is selected when it opens */
  pendingEdiFileNameForClaimTab: string | null = null;
  /** Stack of previously shown panel views (file/letter/claim), used by goBackPanelView(); reset on chat switch/leave. */
  private panelViewHistory: PanelViewHistoryEntry[] = [];
  /** Suppresses history pushes while a setter is called internally to restore/sync a view rather than to navigate to a new one. */
  private isRestoringPanelView: boolean = false;

  // Dashboard statistics (Home page)
  dashboardStatistics: ClaimsDashboardStatistics | null = null;
  isDashboardStatisticsLoading: boolean = false;
  // True only after a failed fetch, so cards can tell "never loaded / no
  // data" apart from "load failed" instead of both rendering as 0.
  hasDashboardStatisticsError: boolean = false;

  // Claim detail (for ClaimPreview)
  claimDetailData: ClaimDetailResponse | null = null;
  isClaimDataLoading: boolean = true;
  claimDetailError: string | null = null;
  previewFileChanged: boolean = false;
  /** Pending trailing-edge refresh scheduled by scheduleClaimRefresh(). */
  private claimRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private claimRefreshTarget: string | null = null;

  // Modals
  chatToShare: IChannel | null = null;
  showDeletePopup: IChannel | null = null;
  showClosePopup: IChannel | null = null;
  showRenamePopup: IChannel | null = null;
  showRenameFilePopup: IRenameFileTarget | null = null;

  claimModeEnabled: boolean = false; // This property controlls the sidebar chat list filtering by claim number
  isSidebarFilterModeEnabled: boolean = false; // Set true when the sidebar filter action button is clicked
  /** Applied "Filter chat history" panel choices (sort/chat type/last active). Set via setSidebarFilters when Apply is clicked. Persisted to localStorage. */
  sidebarFilters: ISidebarFilters = loadSidebarFilters();

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, {
      mainStore: false, // reference only, don't make observable
      claimRefreshTimer: false, // timer handle, nothing renders off it
      claimRefreshTarget: false,
      letterSaveChain: false,
    } as any);
    this.mainStore = mainStore;
    this.activeChatId = ChatHelper.getChatIdFromPathname();
    this.channel = ChatHelper.getChannelTypeFromPathname();

    // Keep apiService in sync with the active chat on EVERY write path
    // (constructor URL init on refresh, reset(), setActiveChatId) so uploads
    // always carry the correct X-Chat-Id for EDI claim match/mismatch.
    reaction(
      () => this.activeChatId,
      (chatId) => apiService.setActiveChatId(chatId),
      { fireImmediately: true },
    );

    // Draft claim chats (Ask Reve) have no chat id yet — send claim number on
    // uploads so EDI ingestion can validate against the claim being discussed.
    reaction(
      () => this.claimIdToPreview,
      (claimNumber) => apiService.setActiveClaimNumber(claimNumber),
      { fireImmediately: true },
    );

    window.addEventListener(WS_EVENTS.MESSAGE_RECEIVED, (e: Event) => {
      this.processReceivedMessage((e as CustomEvent).detail);
    });

    websocketService.on(
      WS_EVENTS.CHAT_CLOSED,
      (data: { chatId: string; closedAt?: string | null }) => {
        if (String(data.chatId) === String(this.activeChatId)) {
          this.markChatClosed(data.closedAt ?? undefined);
        }
      },
    );
  }

  reset() {
    this.cancelUnansweredRecheck();
    // Leaving a chat: its model must not carry into the next draft. A reset
    // from a draft keeps the draft's own pick (see selectedModel).
    if (this.activeChatId) {
      this.selectedModel = DEFAULT_MODEL;
    }
    this.modelChangedForChatId = null;
    this.activeChatId = null;
    this.mainStore?.claimsStore?.setClaimToPreview(null);
    // Bypasses setActiveChatId, which is the usual choke point for this — clear
    // explicitly so a pending predefault question doesn't survive a full reset.
    this.mainStore?.claimsStore?.setClaimActivePredefaultQuestion(null);
    this.isChatLoading = false;
    this.isQuestioning = false;
    this.isAborting = false;
    this.rejectedInput = null;
    this.notice = null;
    this.resetPreviousChat();
  }

  resetPreviousChat() {
    this.cancelUnansweredRecheck();
    // The draft carries its own chat id, so it still saves after we leave.
    void this.saveLetterEdit();
    this.letterTurnAwaitingFinal = false;
    // Don't clear claimIdToPreview here so it survives when navigating from Claims to /channels/[id]
    this.letterToPreview = null;
    this.fileToPreview = null;
    this.claimIdToPreview = null;
    this.channel = "new";
    this.experiments = null;
    this.messages = [];
    this.chatDetails = null;
    this.chatLoadFailure = null;
    this.statusMessages = [];
    this.pendingEdiFileNameForClaimTab = null;
    this.claimDetailData = null;
    this.claimDetailError = null;
    this.isClaimDataLoading = false;
    // A refresh queued for the chat being left must not land on the next one.
    this.cancelClaimRefresh();
    this.lastPollTimestamp = null;
    this.isQuestioning = false;
    this.isAborting = false;
    this.rejectedInput = null;
    this.notice = null;
    this.uploadedFiles = [];
    this.previewFileChanged = false;
    this.panelViewHistory = [];
  }

  resetAskReveal() {
    void this.saveLetterEdit();
    this.letterTurnAwaitingFinal = false;
    // Don't clear claimIdToPreview here so it survives when navigating from Claims to /channels/[id]
    this.letterToPreview = null;
    this.fileToPreview = null;
    this.channel = "new";
    this.experiments = null;
    this.messages = [];
    this.chatDetails = null;
    this.chatLoadFailure = null;
    this.statusMessages = [];
    this.pendingEdiFileNameForClaimTab = null;
    this.cancelClaimRefresh();
    this.lastPollTimestamp = null;
    this.isQuestioning = false;
    this.isAborting = false;
    this.rejectedInput = null;
    this.notice = null;
    this.uploadedFiles = [];
    this.previewFileChanged = false;
    this.panelViewHistory = [];
  }

  /** Expected ref_id for the current response = last user message's server id (from message_ack). Used to ignore stale stream/status. */
  private getExpectedRefId(): string | undefined {
    const lastMeIndex = this.messages.reduce(
      (lastIdx, m, i) => (m.sender === "me" ? i : lastIdx),
      -1,
    );
    return lastMeIndex === -1
      ? undefined
      : (this.messages[lastMeIndex].id ?? undefined);
  }

  /**
   * True when `refId` belongs to a turn this tab already knows about but that is
   * no longer the current one — i.e. a genuinely stale frame from an earlier turn,
   * which must not render or clobber the turn in progress.
   *
   * A ref_id the store has never seen is deliberately NOT treated as stale: it
   * belongs to a turn this tab did not originate (REV-2134 — a turn started
   * through the v2 messages API, from another tab, or by any other integration
   * driving the same chat). Comparing against the last "me" message alone dropped
   * those frames, including the terminal one that clears `isQuestioning`, so the
   * spinner outlived a turn that had already finished. This also covers the window
   * before `message_ack` swaps our optimistic local id for the server id, where
   * the current turn's own ref_id is not yet in the store.
   */
  private isStaleRefId(refId?: string): boolean {
    if (!refId) return false;
    const expectedRefId = this.getExpectedRefId();
    if (!expectedRefId || refId === expectedRefId) return false;
    return this.messages.some((m) => m.sender === "me" && m.id === refId);
  }

  /**
   * True when a frame is evidence that a turn is actually running, and so may
   * arm `isQuestioning`.
   *
   * Only a terminal frame (`final`/`complete`/`error`) clears that flag, so any
   * frame arriving *after* a turn finished that arms it leaves the composer
   * locked behind "Waiting for agent response..." with nothing in flight. Two
   * frames carry no turn at all and used to do exactly that:
   *
   * - `message_ack` is a write receipt. The appeal-letter editor autosaves by
   *   sending a message with `letter_content` and no `content`, which the
   *   backend saves and acks *without* dispatching to the LLM
   *   (`is_auto_save` in api/user_consumer.py) — an ack with no turn behind it.
   * - a `stream` frame with no `text` is the title sidecar the backend emits
   *   for a `chat_updates` frame; the title is consumed off the socket event
   *   itself, and the `stream` case below already ignores it.
   *
   * Neither carries a `ref_id`, so the staleness check above cannot catch them.
   */
  private static indicatesTurnInFlight(message: any): boolean {
    if (message?.type === "message_ack") return false;
    if (message?.type === "stream" && !message?.text) return false;
    return true;
  }

  // Getters
  get currentChannelConfig() {
    const currentChannel = ChatHelper.getChannelTypeFromPathname() || "new";

    return channelsConfig[currentChannel];
  }

  get isPolicyQuestionsChannel() {
    return (
      this.channel === "policy-questions" ||
      window.location.pathname.includes("/policy-questions")
    );
  }

  get isContractResearchChannel() {
    return (
      this.channel === "contract-research" ||
      window.location.pathname.includes("/contract-research")
    );
  }

  get currentChatFiles() {
    const chatFiles = this.chatDetails?.files || [];
    return ChatHelper.extractChatFiles(this.messages, chatFiles);
  }

  /** The document most recently referenced in this chat, read live off
   * message metadata (document_id/document_title) rather than a separate
   * frontend-only field — so it reflects reloaded/reopened chats too, not
   * just the session that created it.
   *
   * Gated on metadata.topic === "Provider Contract Search" (stamped on the
   * first message of a contract-research chat) rather than document_id alone
   * or isContractResearchChannel/pathname, since: (1) Payer Policy Questions
   * chats also stamp document_id on their messages, and (2) after an Ask Reve
   * redirect the route becomes /channels/:id, no longer matching
   * /contract-research. topic survives reload because it's persisted on the
   * message itself. */
  get contractPreviewForChat(): IContractPreviewItem | null {
    const isContractResearchChat = this.messages.some(
      (message) => message.metadata?.topic === "Provider Contract Search"
    );
    if (!isContractResearchChat) {
      return null;
    }

    for (let i = this.messages.length - 1; i >= 0; i--) {
      const metadata = this.messages[i].metadata;
      if (metadata?.document_id) {
        return {
          document_id: metadata.document_id,
          title: metadata.document_title || "",
        };
      }
    }
    return null;
  }

  /** Same idea as contractPreviewForChat, but for Payer Policy Questions chats --
   * gated on metadata.topic === "Payer Policy Questions" (stamped on the first
   * message) for the same reasons documented above contractPreviewForChat.
   * source_url isn't in the message metadata, so it's left blank here -- PolicyPreview
   * only needs document_id (to match/highlight/scroll to the row) and title. */
  get policyPreviewForChat(): IPolicyPreviewItem | null {
    const isPolicyQuestionsChat = this.messages.some(
      (message) => message.metadata?.topic === "Payer Policy Questions"
    );
    if (!isPolicyQuestionsChat) {
      return null;
    }

    for (let i = this.messages.length - 1; i >= 0; i--) {
      const metadata = this.messages[i].metadata;
      if (metadata?.document_id) {
        return {
          document_id: metadata.document_id,
          title: metadata.document_title || "",
          source_url: "",
        };
      }
    }
    return null;
  }

  get chatTag() {
    if (
      window.location.pathname.includes("/appeal-claim") ||
      window.location.pathname.includes("/claims") ||
      this.claimIdToPreview
    ) {
      return "appeal-claim";
    }

    return "unknown";
  }

  get chatFilesDisplay() {
    return this.uploadedFiles || [];
  }

  get isStreaming() {
    return (
      this.messages
        .filter(
          (m) =>
            m.type !== "message_ack" &&
            m.type !== "task_in_progress" &&
            m.type !== "status_message",
        )
        ?.pop()?.type === "stream"
    );
  }
  get isReadOnly() {
    const chatOwnerEmail = this.chatDetails?.user_email;

    return (
      !!chatOwnerEmail && chatOwnerEmail !== this.mainStore?.appStore?.userEmail
    );
  }
  get isClosed() {
    return isChannelClosed(this.chatDetails);
  }
  get isInputDisabled() {
    return this.isReadOnly || this.isClosed;
  }
  /** The last message, when the backend saved it but it never reached the
   * agent (REV-2553) and nothing is running — the UI offers a retry. */
  get retryableMessageId(): string | null {
    const id = this.chatDetails?.unanswered_message_id;
    if (!id || this.isQuestioning || this.isInputDisabled) return null;
    const last = this.messages[this.messages.length - 1];
    return last?.sender === "me" && String(last.id) === String(id)
      ? String(id)
      : null;
  }
  /** The last message while it may still be on its way to the agent. */
  get stillSendingId(): string | null {
    const id = this.stillSendingMessageId;
    if (!id || this.isQuestioning || this.isInputDisabled) return null;
    const last = this.messages[this.messages.length - 1];
    return last?.sender === "me" && String(last.id) === id ? id : null;
  }
  get isFileUploadInProgress() {
    return this.uploadedFiles.some((file) => file.state === "uploading");
  }

  // Files that will attach to the outgoing message. EDI files ingested into
  // this chat's claim are excluded — they live in the claim viewer, not as
  // standalone chat attachments.
  get attachableFiles() {
    return this.uploadedFiles.filter(
      (file) =>
        file.state === "uploaded" &&
        !file.uploadResponse?.claim_ingest?.matched,
    );
  }

  // Sets
  setClaimModeEnabled(state: boolean) {
    this.claimModeEnabled = state
  }
  setIsSidebarFilterModeEnabled(state: boolean) {
    this.isSidebarFilterModeEnabled = state
  }
  setSidebarFilters(filters: ISidebarFilters) {
    runInAction(() => {
      this.sidebarFilters = filters;
    });
    try {
      localStorage.setItem(SIDEBAR_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Ignore localStorage errors
    }
  }
  setActiveChatId(chatId: string | null) {
    runInAction(() => {
      this.activeChatId = chatId;
    });
    websocketService.setActiveChat(chatId);
    // apiService.setActiveChatId is synced by the activeChatId reaction in the
    // constructor, which covers all write paths including reset() and URL init.

    // claimActivePredefaultQuestion is reset manually
    // this.mainStore?.claimsStore?.setClaimActivePredefaultQuestion(null);
  }
  setIsChatLoading(isLoading: boolean) {
    runInAction(() => {
      this.isChatLoading = isLoading;
    });
  }
  setIsQuestioning(isQuestioning: boolean) {
    runInAction(() => {
      this.isQuestioning = isQuestioning;
    });
  }
  setIsAborting(isAborting: boolean) {
    runInAction(() => {
      this.isAborting = isAborting;
    });
  }
  setIsClaimDataLoading(isClaimDataLoading: boolean) {
    runInAction(()=> {
      this.isClaimDataLoading = isClaimDataLoading
    })
  }
  /** Put a just-recorded appeal on the open claim's timeline without waiting for a reload. */
  addClaimAppeal(appeal: ClaimAppeal) {
    runInAction(() => {
      if (!this.claimDetailData) return;
      const appeals = this.claimDetailData.appeals || [];
      if (appeals.some((a) => a.id === appeal.id)) return;
      const { claim } = this.claimDetailData;
      this.claimDetailData = {
        ...this.claimDetailData,
        // A correct and resubmit doesn't make the claim Appealed (REV-2628)
        claim: appeal.actionType === 'correct_resubmit' ? claim : { ...claim, appealed: true },
        appeals: [...appeals, appeal],
      };
    });
  }
  setDashboardStatistics(dashboardStatistics: ClaimsDashboardStatistics | null) {
    runInAction(() => {
      this.dashboardStatistics = dashboardStatistics;
    });
  }
  setIsDashboardStatisticsLoading(isDashboardStatisticsLoading: boolean) {
    runInAction(() => {
      this.isDashboardStatisticsLoading = isDashboardStatisticsLoading;
    });
  }
  setHasDashboardStatisticsError(hasDashboardStatisticsError: boolean) {
    runInAction(() => {
      this.hasDashboardStatisticsError = hasDashboardStatisticsError;
    });
  }
  setPreviewFileChanged(previewFileChanged: boolean) {
    runInAction(() => {
      this.previewFileChanged = previewFileChanged;
    });
  }
  /**
   * `message_ack` rewrites the optimistic bubble's id from local_id to the
   * saved row's id, so a rejection is matched on whichever it still carries.
   */
  discardRejectedMessage(
    localId: string | undefined,
    messageId: string | undefined,
    notice: string,
  ) {
    runInAction(() => {
      const ids = [localId, messageId]
        .filter((id) => id !== undefined && id !== null)
        .map(String);
      const matches = (m: IMessage) => ids.includes(String(m.id));
      const rejected = ids.length
        ? this.messages.find(matches)
        : undefined;
      if (rejected) {
        this.messages = this.messages.filter((m) => !matches(m));
        this.rejectedInput = (rejected as { data?: string }).data ?? null;
      }
      // The composer dropped the attachments on submit and they cannot be
      // put back, so say so rather than let a resend go out without them.
      const files = (rejected as { files?: unknown[] })?.files;
      this.notice =
        files && files.length
          ? `${notice} Re-attach your ${files.length === 1 ? "file" : "files"} before resending.`
          : notice;
    });
  }

  clearNotice() {
    runInAction(() => {
      this.notice = null;
    });
  }

  clearRejectedInput() {
    runInAction(() => {
      this.rejectedInput = null;
    });
  }

  setMessages(messages: IMessage[]) {
    runInAction(() => {
      this.messages = messages;
    });
  }
  setChatDetails(chatDetails: IChannel | null) {
    runInAction(() => {
      this.chatDetails = chatDetails;
    });
  }
  setChatLoadFailure(failure: "not_found" | "failed" | null) {
    runInAction(() => {
      this.chatLoadFailure = failure;
    });
  }
  setExperiments(experiments: ChatExperiments | null) {
    runInAction(() => {
      this.experiments = experiments;
    });
  }

  setSelectedModel(model: string) {
    runInAction(() => {
      this.selectedModel = model;
    });
  }

  /** Whether the active chat has been read, so selectedModel reflects its
   * stored model rather than a leftover from the previous chat. */
  private get isActiveChatRead(): boolean {
    return (
      !!this.activeChatId &&
      !this.isChatLoading &&
      String(this.chatDetails?.id ?? "") === String(this.activeChatId)
    );
  }

  /** Whether ModelSelector may change the model right now. Blocked mid-turn,
   * while a save is in flight, on closed / view-only chats, and on a chat
   * that has not been read yet. A draft with no chat behind it is always
   * changeable — the choice rides the create call. */
  get canChangeModel(): boolean {
    if (this.isQuestioning || this.isSavingModel) return false;
    if (!this.activeChatId) return true;
    return !this.isInputDisabled && this.isActiveChatRead;
  }

  /** The user picked a model in ModelSelector.
   *
   * The backend only reads `model_version` on chat create/update (REST,
   * app-backend #742), not from the WebSocket message, so an existing chat
   * must be updated or the reply would still come from its stored model and a
   * reload would reset the selector. Rolled back if the server refuses. */
  async changeSelectedModel(model: string) {
    if (model === this.selectedModel || !this.canChangeModel) return;
    const previous = this.selectedModel;
    const chatId = this.activeChatId;
    this.setSelectedModel(model);
    if (!chatId) return;

    const previousChangedForChatId = this.modelChangedForChatId;
    runInAction(() => {
      this.isSavingModel = true;
      this.modelChangedForChatId = chatId;
    });
    let saved = false;
    try {
      const response = await apiService.updateChatModelVersion(
        chatId,
        toModelVersion(model),
      );
      // apiService reports a rejected write as success: false, not a throw.
      saved = response.success;
    } catch {
      saved = false;
    } finally {
      runInAction(() => {
        this.isSavingModel = false;
        // Only roll back the chat we wrote to; another chat loaded meanwhile
        // has already set its own model.
        if (!saved && this.activeChatId === chatId) {
          this.selectedModel = previous;
          this.modelChangedForChatId = previousChangedForChatId;
        }
      });
    }
  }

  /** The version sent while model selection is on: "4.5" or "5.5".
   * Undefined when the flag is off, so the payload never carries the field.
   * Null only when the selector holds a value this build does not know. */
  get modelForRequest(): string | null | undefined {
    return this.mainStore?.appStore?.isModelSelectionEnabled
      ? toModelVersion(this.selectedModel)
      : undefined;
  }

  /** The model to send with a message on an existing chat.
   *
   * Only when the user picked it for this chat this session. Otherwise
   * selectedModel may be stale (sent before loadChatMessages finished) or
   * coerced (a stored version this build does not know maps to the rollout),
   * and sending it would overwrite the chat's stored model once the backend
   * reads the field from the message. Create always sends modelForRequest. */
  get modelForMessage(): string | null | undefined {
    return this.activeChatId &&
      this.modelChangedForChatId === this.activeChatId
      ? this.modelForRequest
      : undefined;
  }

  /** Whether this environment offers experiments at all (REV-2161).
   *
   * Read live rather than captured: the flag arrives over the WebSocket after
   * connect, so a value snapshotted during construction would be stale. */
  get areExperimentsEnabled(): boolean {
    return Boolean(
      this.mainStore?.appStore?.configFlags?.[
        APP_CONFIG_FLAGS.CHAT_EXPERIMENTS_ENABLED
      ],
    );
  }

  /** Whether the active chat's stored switches are actually known.
   *
   * `experiments` is null both for a chat that does not exist yet and for one
   * still loading (clearChatStates) or reset mid-session (resetAskReveal, which
   * keeps activeChatId). Only the first can be answered from the environment
   * default; the others we simply have not read yet. */
  get isMockStateKnown(): boolean {
    return this.experiments !== null || !this.activeChatId;
  }

  /** Mocked by default where experiments are enabled, so a developer does not
   * spend a real LLM call just by opening a chat. Only applies to a chat with
   * no recorded choice; anything loaded from the server is taken at face value. */
  get isMockEnabled(): boolean {
    if (this.experiments === null) return this.areExperimentsEnabled;
    return this.experiments.mock === true;
  }

  /** The switches to send when creating this chat.
   *
   * Always explicit — never relying on the server to re-derive the default —
   * so what the toggle shows and what the chat actually does cannot diverge.
   *
   * Gated on the environment as well as the stored choice: the flag is a live
   * kill switch, and the server answers a switch it no longer offers with a 403
   * that fails the whole create. Turning experiments off must cost a user their
   * mock, not their chat. */
  get experimentsForCreate(): Record<string, boolean> {
    return this.areExperimentsEnabled && this.isMockEnabled
      ? { mock: true }
      : {};
  }

  /** Flip the mocked-LLM switch for this chat and persist it.
   *
   * A chat that does not exist yet keeps the choice locally; it is sent with
   * the create call so the very first turn already honours it. */
  async toggleMock() {
    const next: ChatExperiments = this.isMockEnabled ? {} : { mock: true };
    // Turning the switch *on* needs the environment to still offer experiments
    // — the server rejects the write outright. Turning it off is always allowed,
    // so a chat is never stuck carrying a switch nobody honours.
    if (next.mock && !this.areExperimentsEnabled) return;
    const previous = this.experiments;
    // Capture before the write below makes `experiments` non-null and every
    // chat look known.
    const stateKnown = this.isMockStateKnown;
    this.setExperiments(next);

    // Persist only against a chat whose switches we have actually read.
    // activeChatId can point at a chat that was never read (mid-load, or after
    // resetAskReveal), and writing a guessed value there would overwrite the
    // real one. Otherwise the choice stays local and rides the create call.
    if (!this.activeChatId || !stateKnown) return;
    try {
      const response = await apiService.updateChatExperiments(
        this.activeChatId,
        next as Record<string, boolean>,
      );
      // apiService reports a rejected write by returning success: false — a 403
      // from the server-side experiments gate never arrives as a thrown error.
      // Roll back rather than leave the UI claiming a switch the server refused
      // while the chat keeps answering from the other provider.
      if (!response.success) {
        this.setExperiments(previous);
      }
    } catch (error) {
      this.setExperiments(previous);
    }
  }

  markChatClosed(closedAt?: string) {
    runInAction(() => {
      if (this.chatDetails) {
        this.chatDetails = {
          ...this.chatDetails,
          status: "closed",
          ...(closedAt ? { closed_at: closedAt } : {}),
        };
      }
    });
  }
  setStatusMessages(statusMessages: StatusMessageState[]) {
    runInAction(() => {
      this.statusMessages = statusMessages;
    });
  }
  setUploadedFiles(uploadedFiles: AttachedFile[]) {
    runInAction(() => {
      this.uploadedFiles = uploadedFiles;
    });
  }
  setFileToPreview(file: (IFileAttachment & { chatId?: string }) | null) {
    runInAction(() => {
      if (
        file &&
        !this.isRestoringPanelView &&
        file.id !== this.fileToPreview?.id
      ) {
        this.pushCurrentPanelViewToHistory();
      }

      this.fileToPreview = file;

      if (file) {
        // Reset rest
        this.letterToPreview = null;
        this.claimIdToPreview = null;
      }
    });
  }
  setLetterToPreview(letter: ChatLetterOutput | null) {
    runInAction(() => {
      if (
        letter &&
        !this.isRestoringPanelView &&
        letter.id !== this.letterToPreview?.id
      ) {
        this.pushCurrentPanelViewToHistory();
      }

      // Store chat ref for save letter content to the correct chat when switching.
      // An edit re-sets the letter with its chatId already on it, which is kept.
      this.letterToPreview = letter
        ? { ...letter, chatId: letter.chatId ?? (this.activeChatId as string) }
        : null;

      if (letter) {
        // Reset rest
        this.fileToPreview = null;
        this.claimIdToPreview = null;
      }
    });
  }
  setClaimIdToPreview(claimId: string | null, resetPreviewStates = true) {
    runInAction(() => {
      if (
        claimId &&
        !this.isRestoringPanelView &&
        claimId !== this.claimIdToPreview
      ) {
        this.pushCurrentPanelViewToHistory();
      }

      this.claimIdToPreview = claimId;
      if (!claimId) {
        this.pendingEdiFileNameForClaimTab = null;
        this.claimDetailData = null;
        this.claimDetailError = null;
      }

      if (claimId && resetPreviewStates) {
        this.letterToPreview = null;
        this.fileToPreview = null;
      }
    });
  }
  /** Snapshots whichever panel view (file/letter/claim) is currently showing onto the back-history stack. */
  private pushCurrentPanelViewToHistory() {
    if (this.fileToPreview) {
      this.panelViewHistory.push({ type: "file", file: this.fileToPreview });
    } else if (this.letterToPreview) {
      this.panelViewHistory.push({
        type: "letter",
        letter: this.letterToPreview,
      });
    } else if (this.claimIdToPreview) {
      this.panelViewHistory.push({
        type: "claim",
        claimId: this.claimIdToPreview,
      });
    }
  }
  get canGoBackPanelView(): boolean {
    return this.panelViewHistory.length > 0;
  }
  /** Restores the previous panel view (file/letter/claim) from history, if any. */
  goBackPanelView() {
    runInAction(() => {
      const entry = this.panelViewHistory.pop();
      if (!entry) return;

      this.isRestoringPanelView = true;
      if (entry.type === "file") {
        this.setFileToPreview(entry.file);
      } else if (entry.type === "letter") {
        this.setLetterToPreview(entry.letter);
      } else {
        this.setClaimIdToPreview(entry.claimId);
      }
      this.isRestoringPanelView = false;
    });
  }
  /** Call when the panel is deliberately closed (not just navigated away from) so a later, unrelated view doesn't inherit stale back-history. */
  clearPanelViewHistory() {
    runInAction(() => {
      this.panelViewHistory = [];
    });
  }
  setPendingEdiFileNameForClaimTab(fileName: string | null) {
    runInAction(() => {
      this.pendingEdiFileNameForClaimTab = fileName;
    });
  }

  /** Persists the pin flag for the given attachment, then flips it directly on
   * the reference `currentChatFiles` hands out from `messages`/`chatDetails.files`
   * (so the change is observed without re-deriving that list) once the backend
   * confirms it. Returns whether the toggle succeeded. */
  async toggleFilePin(file: IFileAttachment): Promise<boolean> {
    const chatId = this.activeChatId;
    if (!chatId || !file.id) return false;

    const nextPinned = !file.is_pinned;
    try {
      const response = await chatService.toggleFilePin(chatId, file.id, nextPinned);
      if (!response.success) return false;

      runInAction(() => {
        file.is_pinned = response.data?.is_pinned ?? nextPinned;
      });
      return true;
    } catch {
      return false;
    }
  }

  /** Pins or renames a letter version, then applies the confirmed name/pin to
   * every local copy. Only those two fields: the body in the editor may hold
   * an unsaved edit the server's copy would overwrite. */
  private async updateChatLetter(
    letter: ChatLetterSummary,
    changes: Partial<Pick<ChatLetterSummary, "is_pinned" | "display_name">>,
  ): Promise<ApiResponse<ChatLetterOutput>> {
    const chatId = this.activeChatId;
    if (!chatId) return { success: false, error: "No active chat" };

    const response = await chatService.updateChatOutputDocument(
      chatId,
      letter.generation,
      changes,
    );
    if (!response.success || !response.data) return response;

    const meta = {
      is_pinned: response.data.is_pinned,
      display_name: response.data.display_name,
    };
    runInAction(() => {
      if (this.activeChatId !== chatId) return;
      if (this.latestChatLetter?.id === letter.id) {
        this.latestChatLetter = { ...this.latestChatLetter, ...meta };
      }
      if (this.letterToPreview?.id === letter.id) {
        this.letterToPreview = { ...this.letterToPreview, ...meta };
      }
      this.chatLetters = this.chatLetters.map((item) =>
        item.id === letter.id ? { ...item, ...meta } : item,
      );
    });
    return response;
  }

  /** Flips the letter's pin flag. Returns whether the toggle succeeded. */
  async togglePinChatLetter(letter: ChatLetterSummary): Promise<boolean> {
    const response = await this.updateChatLetter(letter, {
      is_pinned: !letter.is_pinned,
    });
    return response.success;
  }

  /** Renames the letter. Returns the response so the caller can surface its error. */
  updateChatLetterName(
    letter: ChatLetterSummary,
    displayName: string,
  ): Promise<ApiResponse<ChatLetterOutput>> {
    return this.updateChatLetter(letter, { display_name: displayName });
  }

  /** Only the chat's latest version can be edited; any other opens read-only. */
  get isLetterPreviewReadOnly(): boolean {
    const letter = this.letterToPreview;
    if (!letter) return false;
    return letter.generation !== this.latestChatLetter?.generation;
  }

  /** Opens one version in the editor. The list carries no bodies, so any
   * version but the latest (already held with its body) is fetched first. */
  async openChatLetter(letter: ChatLetterSummary): Promise<void> {
    const chatId = this.activeChatId;
    if (!chatId) return;

    if (letter.generation === this.latestChatLetter?.generation) {
      this.setLetterToPreview(this.latestChatLetter);
      return;
    }

    const response = await chatService.getChatOutputDocument(chatId, letter.generation);
    if (this.activeChatId !== chatId) return;
    if (!response.success || !response.data) {
      runInAction(() => {
        this.notice = "Could not open this letter. Try again in a moment.";
      });
      return;
    }
    this.setLetterToPreview(response.data);
  }

  /** A new LLM version: refresh the letters, give it a chat bubble, and move
   * an editor that was on the previous latest onto it. The letter's own
   * message is never pushed over the socket, only this event. */
  private async handleDocumentOutputAvailable(event: DocumentOutputAvailableEvent) {
    const chatId = String(event.chat_id);
    const editorWasOnLatest =
      this.letterToPreview?.chatId === chatId && !this.isLetterPreviewReadOnly;
    runInAction(() => {
      this.letterTurnAwaitingFinal = true;
    });

    await this.loadChatLetters(chatId);

    const latest = this.latestChatLetter;
    if (this.activeChatId !== chatId || !latest) return;
    runInAction(() => {
      if (!this.messages.some((m) => m.id === latest.message)) {
        this.messages = [
          ...this.messages,
          ChatHelper.normalizeMessage(chatId, {
            id: latest.message,
            role: "assistant",
            content: "",
            agent_name: latest.agent_name,
            created_at: latest.created_at,
            files: [],
          } as RawMessage),
        ];
      }
      if (editorWasOnLatest) {
        if (this.letterDraft?.chatId === chatId) {
          // Written against the version before this one: saving it now would
          // be refused as stale, so say so instead of failing later.
          this.letterDraft = null;
          this.notice =
            "A new version of the letter was generated, so your unsaved change was not applied to it. The new version is now open.";
        }
        this.letterToPreview = { ...latest, chatId };
      }
    });
  }

  /** Records an editor change to the open letter; saveLetterEdit persists it. */
  noteLetterEdit(content: string) {
    const letter = this.letterToPreview;
    if (!letter?.chatId || this.isLetterPreviewReadOnly) return;
    this.letterDraft = {
      chatId: letter.chatId,
      generation: letter.generation,
      content,
    };
    this.letterToPreview = { ...letter, content };
  }

  /** Saves the pending letter edit, if any. False when it did not land — the
   * user has already been told why — so a caller can refuse to go on (e.g. a
   * download that would otherwise be a version behind). */
  saveLetterEdit(): Promise<boolean> {
    const run = this.letterSaveChain.then(() => this.persistLetterDraft());
    this.letterSaveChain = run.catch(() => false);
    return run;
  }

  private async persistLetterDraft(): Promise<boolean> {
    const draft = this.letterDraft;
    if (!draft) return true;
    if (ChatHelper.isLetterContentEmpty(draft.content)) {
      // The backend refuses an empty letter; leave the stored one as it was.
      runInAction(() => {
        if (this.letterDraft === draft) this.letterDraft = null;
      });
      return true;
    }

    const response = await chatService.updateChatOutputDocument(
      draft.chatId,
      draft.generation,
      { content: draft.content },
    );

    if (response.success && response.data) {
      const saved = response.data;
      runInAction(() => {
        // A keystroke made while this save was in flight keeps its own draft.
        if (this.letterDraft === draft) this.letterDraft = null;
        if (this.activeChatId !== draft.chatId) return;
        if (this.latestChatLetter?.generation === saved.generation) {
          this.latestChatLetter = { ...this.latestChatLetter, ...saved };
        }
      });
      return true;
    }

    const body = response.data as { error?: string } | undefined;
    if (response.status === 409 && body?.error === "document_output_stale") {
      // A new LLM version landed while the user was editing; nothing was written.
      runInAction(() => {
        if (this.letterDraft === draft) this.letterDraft = null;
        this.notice =
          "A new version of the letter was generated while you were editing, so your last change was not applied to it. The new version is now open.";
      });
      await this.loadChatLetters(draft.chatId);
      runInAction(() => {
        const latest = this.latestChatLetter;
        if (latest && this.letterToPreview?.chatId === draft.chatId) {
          this.letterToPreview = { ...latest, chatId: draft.chatId };
        }
      });
      return false;
    }

    if (response.status === 409) {
      runInAction(() => {
        if (this.letterDraft === draft) this.letterDraft = null;
        this.notice = "This chat is closed, so the letter can no longer be edited.";
      });
      return false;
    }

    if (response.status && response.status >= 400 && response.status < 500) {
      // Retrying will not change the answer (not the owner, letter gone, …).
      runInAction(() => {
        if (this.letterDraft === draft) this.letterDraft = null;
        this.notice = response.error || "Your letter changes could not be saved.";
      });
      return false;
    }

    // Network or server error: kept, so the next save (or flush) retries it.
    runInAction(() => {
      this.notice = "Could not save your latest letter changes. They will be retried.";
    });
    return false;
  }

  /** Applies a new display name to the given attachment reference (the same
   * object `currentChatFiles` hands out) once {@link RenameFileDialog} has
   * confirmed the rename with the backend. */
  applyFileRename(file: IFileAttachment, newName: string) {
    runInAction(() => {
      file.display_name = newName;
    });
  }

  selectFileToPreview(file: IFileAttachment, originalFileName: string) {
    const claimId =
      (this.chatDetails as any)?.claim_id ??
      this.chatDetails?.claim_number ??
      null;

    const claimData = this.claimDetailData || this.chatDetails?.claim || null;
    const ediObjects = claimData?.ediObjects;
    const isEdiFileMatch = ediObjects?.length && isEdiFile(originalFileName);

    if (claimId && isEdiFileMatch) {
      this.setPendingEdiFileNameForClaimTab(originalFileName);
      this.claimDetailData = claimData as ClaimDetailResponse | null;
      this.setClaimIdToPreview(claimId);

      // Wait for panel to open
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("selectEdiTab", {
            detail: {
              ...(file || {}),
              fileName: originalFileName,
              claim: this.chatDetails?.claim,
            },
          }),
        );
      }, 100);
    } else {
      this.setFileToPreview(file);
    }
  }
  setChatToShare(chat: IChannel | null) {
    runInAction(() => {
      this.chatToShare = chat;
    });
  }
  setShowDeletePopup(chat: IChannel | null) {
    runInAction(() => {
      this.showDeletePopup = chat;
    });
  }
  setShowClosePopup(chat: IChannel | null) {
    runInAction(() => {
      this.showClosePopup = chat;
    });
  }
  setShowRenamePopup(chat: IChannel | null) {
    runInAction(() => {
      this.showRenamePopup = chat;
    });
  }
  setShowRenameFilePopup(target: IRenameFileTarget | null) {
    runInAction(() => {
      this.showRenameFilePopup = target;
    });
  }

  // STATE ACTIONS
  clearChatStates() {
    this.setChatDetails(null);
    this.setChatLoadFailure(null);
    this.setMessages([]);
    this.setUploadedFiles([]);
    this.chatLetters = [];
    this.latestChatLetter = null;
    // Every chat switch lands here, including the URL/search routes that never
    // call resetPreviousChat — a refresh queued for the chat being left would
    // otherwise fetch its claim into the one being opened.
    this.cancelClaimRefresh();
    // Drop the previous chat's switches here rather than only on a successful
    // load: if loading the next chat fails, or while it is still in flight, a
    // leftover choice would have the toggle describing one chat while pointing
    // at another — and a click would then PATCH the new chat with it.
    this.setExperiments(null);
    // Same for the model: the next chat's load adopts its own.
    this.resetModelToDefault();
  }

  /** Back to the default model, forgetting any per-chat user change. Used
   * wherever the active chat's stored model is no longer known. */
  resetModelToDefault() {
    runInAction(() => {
      this.selectedModel = DEFAULT_MODEL;
      this.modelChangedForChatId = null;
    });
  }

  silentClearChatStore() {
    this.cancelUnansweredRecheck();
    this.setChatDetails(null);
    this.setChatLoadFailure(null);
    this.setUploadedFiles([]);
    this.chatLetters = [];
    this.latestChatLetter = null;
    // Every chat switch lands here, including the URL/search routes that never
    // call resetPreviousChat — a refresh queued for the chat being left would
    // otherwise fetch its claim into the one being opened.
    this.cancelClaimRefresh();
    // Drop the previous chat's switches here rather than only on a successful
    // load: if loading the next chat fails, or while it is still in flight, a
    // leftover choice would have the toggle describing one chat while pointing
    // at another — and a click would then PATCH the new chat with it.
    this.setExperiments(null);
    // Same for the model: the load that follows adopts the chat's own.
    this.resetModelToDefault();
  }
  clearWarningMessage() {
    runInAction(() => {
      this.messages = this.messages.map((msg) => ({
        ...msg,
        contextWarning: false,
      }));
    });
  }
  // QUERIES
  async loadClaimDetail(
    claimNumber: string,
  ): Promise<ClaimDetailResponse | null> {
    runInAction(() => {
      this.claimDetailError = null;
    });

    try {
      const data = await apiService.getClaimDetail(claimNumber);
      runInAction(() => {
        this.claimDetailData = data;
        this.chatDetails = {
          ...(this.chatDetails || {}),
          claim: data,
        } as IChannel;
      });
      return data;
    } catch (err) {
      runInAction(() => {
        this.claimDetailError =
          err instanceof Error ? err.message : "Failed to load claim details";
        this.claimDetailData = null;
      });
      return null;
    } finally {
      runInAction(() => {
        this.isClaimDataLoading = false;
      });
    }
  }

  /** Refetch the open claim after an edit (appeal, manual payment) changed it.
   *
   * Unlike loadClaimDetail it never clears what's shown: a failed refetch keeps
   * the current data (which may already hold the edit, see addClaimAppeal), and a
   * response for a claim the user has since left is dropped rather than shown
   * under the claim now open. Returns the fresh data only when it was applied. */
  async refreshClaimDetail(
    claimNumber: string,
  ): Promise<ClaimDetailResponse | null> {
    const queuedForPreview = this.claimIdToPreview;
    const isStillOpen = () =>
      this.claimIdToPreview === queuedForPreview &&
      this.claimDetailData?.claim?.claimNumber === claimNumber;
    if (!isStillOpen()) return null;

    let data: ClaimDetailResponse;
    try {
      data = await apiService.getClaimDetail(claimNumber);
    } catch {
      return null;
    }
    if (!data || !isStillOpen()) return null;
    runInAction(() => {
      this.claimDetailData = data;
      this.chatDetails = {
        ...(this.chatDetails || {}),
        claim: data,
      } as IChannel;
    });
    return data;
  }

  /** Fetches the chat's generated documents (appeal letters) into `chatLetters`
   * and its most recent one into `latestChatLetter`. The two requests are
   * independent: a failure in one leaves the other's result intact. A response
   * for a chat the user has since left is dropped rather than written over the
   * chat now open. */
  async loadChatLetters(
    chatId: string | null = this.activeChatId,
  ): Promise<ChatLetterSummary[] | null> {
    if (!chatId) return null;

    runInAction(() => {
      this.isChatLettersLoading = true;
    });
    try {
      // Each call is caught on its own so one rejecting can't reject the
      // Promise.all and discard the other's response.
      const [lettersResponse, latestResponse] = await Promise.all([
        chatService.getChatOutputDocuments(chatId).catch(() => null),
        chatService.getLatestChatOutputDocument(chatId).catch(() => null),
      ]);

      const letters = lettersResponse?.success ? lettersResponse.data ?? [] : null;
      // 404 is "no documents yet"; any other failure keeps what we had.
      const latest = latestResponse?.success
        ? latestResponse.data ?? null
        : latestResponse?.status === 404
          ? null
          : undefined;

      runInAction(() => {
        if (this.activeChatId !== chatId) {
          return;
        }
        
        if (letters) {
          this.chatLetters = letters;
        }
        if (latest !== undefined) {
          this.latestChatLetter = latest;
        }
      });

      return letters;
    } finally {
      runInAction(() => {
        this.isChatLettersLoading = false;
      });
    }
  }

  async loadDashboardStatistics(): Promise<ClaimsDashboardStatistics | null> {
    this.setIsDashboardStatisticsLoading(true);
    this.setHasDashboardStatisticsError(false);

    try {
      const data = await apiService.getDashboardStatistics();
      this.setDashboardStatistics(data);
      return data;
    } catch (error) {
      this.setDashboardStatistics(null);
      this.setHasDashboardStatisticsError(true);
      return null;
    } finally {
      this.setIsDashboardStatisticsLoading(false);
    }
  }

  /** Refetch the chat's claim shortly after an EDI upload changed it.
   *
   * Trailing-edge debounce: a user drops a whole claim packet at once and each
   * file arrives as its own `file_uploaded` frame, so firing per frame would
   * mean N fetches of the same claim, the last few racing each other. Waiting
   * also covers the gap between the EDI landing in the claim and the derived
   * data (status recompute, log entries) being readable.
   *
   * The chat the refresh was queued for is captured and rechecked when it
   * fires: a user who navigates away inside the delay window must not have the
   * chat they landed on repainted with the claim they left, and loadClaimDetail
   * writes chatDetails.claim, so a late fetch would do exactly that. The reset
   * paths cancel outright; this guard covers whatever path they miss.
   */
  scheduleClaimRefresh(
    claimNumber: string,
    delayMs: number = CLAIM_REFRESH_DEBOUNCE_MS,
  ) {
    if (!claimNumber) return;
    this.cancelClaimRefresh();
    const queuedForChatId = this.activeChatId;
    this.claimRefreshTarget = claimNumber;
    this.claimRefreshTimer = setTimeout(() => {
      this.claimRefreshTimer = null;
      this.claimRefreshTarget = null;
      if (this.activeChatId !== queuedForChatId) return;
      void this.loadClaimDetail(claimNumber);
    }, delayMs);
  }

  /** Claim number this chat is about, whether or not the panel is open. */
  get chatClaimNumber(): string | null {
    const details = this.chatDetails as {
      claim_number?: string | null;
      claim_id?: string | null;
    } | null;
    return (
      this.claimIdToPreview ||
      details?.claim_number ||
      details?.claim_id ||
      null
    );
  }

  /** An EDI attachment rewrote a claim server-side — refresh the panel.
   *
   * Only ever the chat's own claim. `claim_number` on the frame is set only
   * when the file landed in that claim, so an EDI belonging elsewhere is
   * ignored — it changed nothing the user is looking at. A frame that never
   * got an ingest outcome (older backend, or an ingest that errored) names no
   * claim either way, so it falls back to the chat's, which is what an
   * attachment here almost always touches.
   */
  private handleUploadedClaimData(message: {
    is_edi?: boolean;
    claim_ingested?: boolean;
    claim_number?: string | null;
  }) {
    if (!message?.is_edi) return;

    const chatClaim = this.chatClaimNumber;
    if (!chatClaim) return;
    if (message.claim_ingested && message.claim_number !== chatClaim) return;

    this.scheduleClaimRefresh(chatClaim);
  }

  cancelClaimRefresh() {
    if (this.claimRefreshTimer) {
      clearTimeout(this.claimRefreshTimer);
      this.claimRefreshTimer = null;
    }
    this.claimRefreshTarget = null;
  }

  async loadChatMessages(
    chatId: string,
  ): Promise<ChatSession | undefined> {
    try {
      // Owned here, not by callers: without it a load renders as an empty chat.
      this.setIsChatLoading(true);
      // Clear previous chat data before loading next chat (leave messages for ask Reve flow)
      // Full clear will be done manually when clicking a chat from sidebar or from Header
      this.silentClearChatStore();
      // Switching chats invalidates any panel view history built up in the previous chat
      this.panelViewHistory = [];
      // silentClearChatStore put the model back to the default; the success
      // path below adopts this chat's stored one.

      // Reconciliation poll below starts from here so nothing landing during
      // getChat is missed.
      const sinceBeforeLoad = new Date().toISOString();
      let reconcileInFlight = false;

      const chatData = await chatService.getChat(chatId);

      if (chatData.success && chatData.data?.id) {
        const { messages, ...chat } = chatData.data;
        const chatDetails = chat as unknown as IChannel;

        const rawStatusMessages =
          chatData?.data?.messages?.filter(
            (message) => message.role === "status",
          ) || [];

        const chatMessages =
          (chatData?.data?.messages
            ?.filter((message) => message.role !== "status")
            ?.map((message) =>
              ChatHelper.normalizeMessage(
                chatId,
                message as RawMessage,
              ),
            ) as unknown as IMessage[]) || [];

        // Convert API status messages to StatusMessageState, then for same tool keep only the last; only add if phase is start or progress
        const statusStates = rawStatusMessages
          .map((msg) => ChatHelper.statusFromMessage(msg as RawMessage))
          .filter((s): s is StatusMessageState => s != null);

        // Order-insensitive: a tool's complete row can persist before its start
        // row (multi-worker race, REV-2419). `complete`/`failed` both terminal (REV-2243).
        const toAdd = ChatHelper.inFlightStatuses(
          statusStates,
          ChatHelper.lastTerminalAssistantAt(chatData.data.messages || []),
        );
        if (toAdd.length > 0) {
          this.setStatusMessages(toAdd);
        }

        // The processing lock is authoritative; in-flight status rows are a
        // proxy that misses a turn which never opened one, leaving the composer
        // live mid-turn (REV-2339). Absent against an older backend and null
        // when Redis cannot say — fall back to the proxy then.
        const processing = (chatDetails as { processing?: boolean | null })
          .processing;
        if (typeof processing === "boolean") {
          this.setIsQuestioning(processing);
        } else if (toAdd.length > 0) {
          this.setIsQuestioning(true);
        }

        const claimIdFromChat =
          (chatDetails as any).claim_id ??
          (chatDetails as any).claim_number ??
          null;

        this.setMessages(chatMessages || []);
        this.setExperiments(chatDetails?.experiments || {},);
        // Reflect the chat's own model in the selector (null = default).
        this.setSelectedModel(fromModelVersion(chat.model_version));
        // The stored model is now the source of truth again; don't echo it
        // (possibly coerced) back with every message.
        runInAction(() => {
          this.modelChangedForChatId = null;
        });
        this.setChatDetails(chatDetails);
        this.setChatLoadFailure(null);
        this.lastPollTimestamp = new Date().toISOString();
        reconcileInFlight = toAdd.length > 0;
        // Sync-only: don't let a leftover preview from the previous chat get pushed into the fresh history
        this.isRestoringPanelView = true;
        this.setClaimIdToPreview(claimIdFromChat);
        this.isRestoringPanelView = false;
        if (claimIdFromChat) {
          this.loadClaimDetail(claimIdFromChat);
        }

        void this.loadChatLetters(chatId);
        this.scheduleUnansweredRecheck(chatId);
      } else {
        this.setClaimIdToPreview(null);
        this.setExperiments(null);
        // Only 403/404 mean the chat is absent or unshared. Guarded on the
        // active chat, like the reconcile below: nothing cancels a stale load.
        if (this.activeChatId === chatId) {
          // Never leave the previous chat's model selected on a failed load.
          this.resetModelToDefault();
          this.setChatLoadFailure(
            chatData.status === 403 || chatData.status === 404
              ? "not_found"
              : "failed",
          );
        }
      }

      this.setIsChatLoading(false);

      // REV-2396: a start/progress status row can be stale (terminal write is
      // best-effort server-side). Check the processing lock now instead of
      // waiting for the watchdog; runs last so a chat switch mid-poll can't
      // interleave with the synchronous tail above.
      if (reconcileInFlight && this.activeChatId === chatId) {
        this.lastPollTimestamp = sinceBeforeLoad;
        await this.pollNewMessages();
      }

      return chatData?.data;
    } catch (error) {
      console.error(error);
      this.setMessages([]);
      // The try also covers the tail that runs once the chat is on screen.
      if (!this.chatDetails?.id && this.activeChatId === chatId) {
        this.resetModelToDefault();
        this.setChatLoadFailure("failed");
      }
      this.setIsChatLoading(false);
    }
  }

  /** Poll for new messages. Returns is_processing for smart interval.
   *
   * Used by read-only guest viewers for their whole session, and by the owner
   * as an in-flight watchdog (REV-2348 / REV-2422) — the websocket is otherwise
   * the only thing that clears `isQuestioning` / `isAborting`, and the backend
   * skips channel delivery when the socket is down (`user_not_connected`).
   * Safe for both: new messages are deduped by id below, so a frame the socket
   * already delivered is ignored.
   */
  async pollNewMessages(): Promise<boolean> {
    const chatId = this.activeChatId;
    if (!chatId) {
      return false;
    }

    // New chats created in askQuestion skip loadChatMessages, which is the
    // usual place lastPollTimestamp is armed. Falling back to epoch still
    // queries X-Chat-Processing (the watchdog signal) and backfills frames
    // the socket missed; existing messages are deduped by id below.
    const since = this.lastPollTimestamp ?? new Date(0).toISOString();

    try {
      const result = await apiService.pollChatMessages(chatId, since);
      // Chat switched while in flight — this answer is not for the active chat.
      if (this.activeChatId !== chatId) return false;
      if (!result.success || !result.data) return false;

      const { messages: newRaw, is_processing: isProcessing } = result.data;

      if (newRaw && newRaw.length > 0) {
        const existingIds = new Set(this.messages.map((m) => m.id));

        // An optimistic row still carrying its `local-` id is invisible to the
        // id dedupe below, so the poll would append the server's copy of a
        // message the user is already looking at — the duplicate upload
        // bubbles in REV-2353. `message_ack` is what normally swaps the id;
        // this covers whatever path misses it. Adopt the server id onto the
        // local row instead of appending, matching on author + text (a user
        // cannot have two unreconciled sends of identical text in flight).
        const unreconciled = this.messages.filter(
          (m) => typeof m.id === "string" && m.id.startsWith("local-"),
        );
        if (unreconciled.length > 0) {
          const adopted = new Map<string, string>();
          for (const raw of newRaw as any[]) {
            if (raw.role !== "user") continue;
            const match = unreconciled.find(
              (m) =>
                !adopted.has(m.id as string) &&
                m.sender === "me" &&
                String(m.data ?? "") === String(raw.content ?? ""),
            );
            if (match) {
              adopted.set(match.id as string, raw.id);
              existingIds.add(raw.id);
            }
          }
          if (adopted.size > 0) {
            this.setMessages(
              this.messages.map((m) =>
                adopted.has(m.id as string)
                  ? ({ ...m, id: adopted.get(m.id as string) } as IMessage)
                  : m,
              ),
            );
          }
        }

        // Separate status vs regular messages
        const newStatusRaw = newRaw.filter((m: any) => m.role === "status");
        // The letter reuses the notice's saved id. Let that row through so the
        // fold can replace the notice; a second copy of the same body stays out.
        const newRegular = newRaw.filter((m: any) => {
          if (m.role === "status") return false;
          if (!existingIds.has(m.id)) return true;
          if (!m.metadata?.aborted || !m.metadata?.partial_content_length) {
            return false;
          }
          const shown = this.messages.find((row) => row.id === m.id);
          return Boolean(shown && !shown.metadata?.partial_content_length);
        });

        // Append new regular messages. A stop frame is folded in, not appended:
        // the socket may already be showing it, or the live stream bubble may
        // still be on screen.
        if (newRegular.length > 0) {
          const normalized = newRegular.map(
            (m: any) =>
              ChatHelper.normalizeMessage(
                this.activeChatId as string,
                m as RawMessage,
              ) as unknown as IMessage,
          );
          let next = this.messages;
          const pendingStops: IMessage[] = [];
          for (const message of normalized) {
            if (message.metadata?.aborted) pendingStops.push(message);
            else next = [...next, message];
          }
          for (const message of pendingStops) {
            next = this.applyAbortFinal(
              next,
              message,
              Boolean(message.metadata?.partial_content_length),
            );
          }
          this.setMessages(next);
          // Stop poisons isAborting until a terminal frame arrives; after Stop
          // the lock is released immediately so isQuestioning clears first and
          // the watchdog would stop — only a polled terminal row clears this
          // (REV-2422).
          if (newRegular.some((m) => ChatHelper.isTerminalAssistantRow(m))) {
            this.setIsAborting(false);
          }
        }

        // Merge over what's already showing, not just this delta (REV-2419).
        if (newStatusRaw.length > 0) {
          const statusStates = newStatusRaw
            .map((msg: any) => ChatHelper.statusFromMessage(msg as RawMessage))
            .filter((s): s is StatusMessageState => s != null);

          const polledTools = new Set(statusStates.map((s) => s.tool));
          const untouchedLive = this.statusMessages.filter(
            (s) => !polledTools.has(s.tool),
          );
          const toAdd = ChatHelper.inFlightStatuses(
            [...untouchedLive, ...statusStates],
            ChatHelper.lastTerminalAssistantAt(this.messages),
          );
          this.setStatusMessages(toAdd);
        }

        this.lastPollTimestamp = new Date().toISOString();
      }

      // null = lock state unknown (Redis unreachable); keep current state.
      if (isProcessing === null) {
        return this.isQuestioning;
      }

      // Clear status messages when processing is done
      if (!isProcessing && this.statusMessages.length > 0) {
        this.setStatusMessages([]);
      }

      const turnEnded = this.isQuestioning && !isProcessing;
      runInAction(() => {
        this.isQuestioning = isProcessing;
      });
      // The turn ended without a frame reaching this tab (e.g. a dropped
      // socket): re-read whether our message went unanswered (REV-2553).
      if (turnEnded) {
        this.scheduleUnansweredRecheck(chatId, true);
      }

      return isProcessing;
    } catch {
      return false;
    }
  }

  /** Stop frame whose ref is a Stop message already in the transcript. */
  private isAbortForKnownStop(message: {
    ref_id?: string;
    metadata?: { aborted?: boolean; ref_id?: string };
  }): boolean {
    if (!message?.metadata?.aborted) return false;
    const refId = message.ref_id || message.metadata.ref_id;
    if (!refId) return false;
    return this.messages.some(
      (m) => m.sender === "me" && m.id === refId && !!m.metadata?.abort,
    );
  }

  /** Stop user-message id. The socket sends it on the frame; a saved row keeps it on metadata. */
  private stopRefOf(frame: IMessage): string | undefined {
    return frame.ref_id || frame.metadata?.ref_id;
  }

  /** Stop this frame names.
   *
   * A ref that is not on screen yet can only be the Stop still waiting for its
   * ack, so it falls back to the one Stop with a local id. A frame with no ref
   * is not placed.
   */
  private stopIndexFor(messages: IMessage[], stopRef?: string): number {
    if (!stopRef) return -1;
    const byId = messages.findIndex(
      (m) => m.sender === "me" && m.id === stopRef,
    );
    if (byId >= 0) return byId;
    const pending: number[] = [];
    messages.forEach((m, i) => {
      if (
        m.sender === "me" &&
        m.metadata?.abort &&
        String(m.id).startsWith("local-")
      ) {
        pending.push(i);
      }
    });
    return pending.length === 1 ? pending[0] : -1;
  }

  /** Assistant bubble directly above this Stop. */
  private holdBeforeStop(messages: IMessage[], stopIdx: number): number {
    for (let i = stopIdx - 1; i >= 0; i--) {
      const message = messages[i];
      if (!message || message.sender === "me") break;
      return i;
    }
    return -1;
  }

  /** The letter for this Stop is already under it. */
  private letterUnderStop(messages: IMessage[], stopIdx: number): boolean {
    for (let i = stopIdx + 1; i < messages.length; i++) {
      const message = messages[i];
      if (!message || message.sender === "me") break;
      if (message.metadata?.aborted && message.metadata.partial_content_length) {
        return true;
      }
    }
    return false;
  }

  /** The short stop notice for this Stop is already under it. */
  private noticeUnderStop(messages: IMessage[], stopIdx: number): boolean {
    for (let i = stopIdx + 1; i < messages.length; i++) {
      const message = messages[i];
      if (!message || message.sender === "me") break;
      if (
        message.metadata?.aborted &&
        !message.metadata.partial_content_length
      ) {
        return true;
      }
    }
    return false;
  }

  /** Stream kept on screen until the stop letter replaces it. */
  private streamHeldForPendingStop(
    messages: IMessage[],
    streamIdx: number,
  ): boolean {
    for (let i = streamIdx + 1; i < messages.length; i++) {
      const message = messages[i];
      if (!message) continue;
      if (message.sender !== "me") continue;
      if (message.metadata?.abort) {
        return !this.letterUnderStop(messages, i);
      }
      return false;
    }
    return false;
  }

  /** Fold one stop frame under the Stop it names.
   *
   * The socket and the poll each deliver it. The notice leaves the streamed
   * text on screen. The letter replaces that bubble and the notice.
   */
  private applyAbortFinal(
    messages: IMessage[],
    normalized: IMessage,
    carriesPartial: boolean,
  ): IMessage[] {
    // Same id is a repeat, unless this frame is the letter replacing the notice
    // that was saved as this row.
    const already = normalized.id
      ? messages.find((m) => m.id === normalized.id)
      : undefined;
    const upgrading =
      Boolean(already) &&
      carriesPartial &&
      !already?.metadata?.partial_content_length;
    if (already && !upgrading) return messages;

    const working = already
      ? messages.filter((m) => m.id !== already.id)
      : messages;
    const stopIdx = this.stopIndexFor(working, this.stopRefOf(normalized));
    if (stopIdx < 0) return messages;

    if (!carriesPartial) {
      if (this.letterUnderStop(working, stopIdx)) return messages;
      if (this.noticeUnderStop(working, stopIdx)) return messages;
      let insertAt = working.length;
      for (let i = stopIdx + 1; i < working.length; i++) {
        if (working[i]?.sender === "me") {
          insertAt = i;
          break;
        }
      }
      return [...working.slice(0, insertAt), normalized, ...working.slice(insertAt)];
    }

    const drop = new Set<number>();
    const hold = this.holdBeforeStop(working, stopIdx);
    if (hold >= 0 && working[hold]?.type === WEBSOCKET_MESSAGE_TYPES.stream) {
      drop.add(hold);
    }
    let insertAt = working.length;
    for (let i = stopIdx + 1; i < working.length; i++) {
      const message = working[i];
      if (!message || message.sender === "me") {
        if (message?.sender === "me") insertAt = i;
        break;
      }
      if (
        message.metadata?.aborted &&
        !message.metadata.partial_content_length
      ) {
        drop.add(i);
      }
    }

    const kept = working.filter((_, i) => !drop.has(i));
    const removedBefore = [...drop].filter((i) => i < insertAt).length;
    const at = insertAt - removedBefore;
    return [...kept.slice(0, at), normalized, ...kept.slice(at)];
  }

  processReceivedMessage(message: any) {
    // Only process messages for the currently active chat; use String() so we don't
    // accidentally process another chat's events (e.g. when types differ or when activeChatId is null).
    const messageChatId = message?.chat_id;
    if (
      !this.activeChatId ||
      !messageChatId ||
      String(messageChatId) !== String(this.activeChatId)
    ) {
      return;
    }

    // V2 attachment upload — merge file into chatDetails so the sidebar
    // shows it immediately without a full reload.
    if (
      message.type === WEBSOCKET_MESSAGE_TYPES.file_uploaded &&
      message.file
    ) {
      runInAction(() => {
        if (this.chatDetails) {
          const existing = this.chatDetails.files || [];
          if (!existing.some((f) => f.id === message.file.id)) {
            this.chatDetails = {
              ...this.chatDetails,
              files: [...existing, message.file],
            };
          }
        }
      });
      this.handleUploadedClaimData(message);
      return;
    }

    // The LLM added a document version. Not a turn frame: it must not arm the
    // spinner, and it carries no ref_id for the staleness check below.
    if (message.type === WEBSOCKET_MESSAGE_TYPES.document_output_available) {
      void this.handleDocumentOutputAvailable(message as DocumentOutputAvailableEvent);
      return;
    }

    // Drop frames belonging to a turn that is no longer current, before touching
    // any shared state. This must precede `setIsQuestioning(true)` below: that
    // call is unconditional, so a stale frame returning after it would switch the
    // spinner on with nothing in flight and never switch it back off (the error
    // case in particular used to clear it on the way out).
    // A late stop frame names the Stop message, which is an older user message
    // once the next question is last. That id must still reach the fold below.
    if (
      this.isStaleRefId(message.ref_id || message.metadata?.ref_id) &&
      !this.isAbortForKnownStop(message)
    ) {
      return;
    }

    if (ChatStore.indicatesTurnInFlight(message)) {
      this.setIsQuestioning(true);
    }

    const messageRotation = message?.page_rotation_mapping as Record<
      string,
      IPageRotation
    >;

    if (messageRotation && Object.keys(messageRotation)?.length > 0) {
      // Update file rotation for the message
      runInAction(() => {
        this.messages = this.messages.map((currentMessage) => {
          if (
            currentMessage?.files &&
            currentMessage?.files?.length > 0 &&
            currentMessage?.files?.some((f) => !!messageRotation?.[f.id])
          ) {
            return {
              ...currentMessage,
              // Update file rotation
              files: currentMessage.files.map((f) =>
                messageRotation?.[f.id]
                  ? { ...f, page_rotation_mapping: messageRotation?.[f.id] }
                  : f,
              ),
            };
          }
          return currentMessage;
        });
        const preview = this.fileToPreview;
        if (preview && messageRotation?.[preview.id]) {
          this.fileToPreview = {
            ...preview,
            page_rotation_mapping: messageRotation?.[preview.id],
          };
        }
      });
    }

    switch (message.type) {
      case WEBSOCKET_MESSAGE_TYPES.stream: {
        const text = message.text || "";
        if (!text) break;

        const sameTurn = (m: IMessage) =>
          m.type === WEBSOCKET_MESSAGE_TYPES.stream &&
          (!message.ref_id || m.ref_id === message.ref_id || m.id === message.ref_id);

        if (!this.messages.some(sameTurn)) {
          this.setMessages([
            ...this.messages,
            {
              ...message,
              id: message.ref_id,
              channel: this.activeChatId as string,
              data: text,
              sender: "other",
              timestamp: Date.now(),
            },
          ]);
          return;
        }

        runInAction(() => {
          this.messages = this.messages.map((m) =>
            // If text is object (claims details), keep the existing data as is
            // Claims details object is streamed once
            sameTurn(m)
              ? {
                  ...m,
                  data: typeof text === "string" ? (m.data || "") + text : text,
                }
              : m,
          );
        });
        break;
      }
      case WEBSOCKET_MESSAGE_TYPES.partial:
      case WEBSOCKET_MESSAGE_TYPES.complete:
      case WEBSOCKET_MESSAGE_TYPES.final:
      // Finished mid-turn agent payload (REV-2296/REV-2298): app-backend persists
      // it as its own row and does not release the processing lock, so it renders
      // like a partial/final but must not unlock the composer. Sent verbatim over
      // the v1 WebSocket.
      case WEBSOCKET_MESSAGE_TYPES.agent_result: {
        const refId = message.ref_id;
        const metadata: any = message.metadata || {};
        if (message.policies) metadata.policies = message.policies;
        if (message.context_warning || message.contextWarning) {
          metadata.contextWarning = true;
        }

        const raw: RawMessage = {
          ...message,
          id: message.id || `local-${Date.now()}`,
          // The letter-only stub text is derived in ChatHelper.normalizeMessage
          // so the live frame and a reloaded/polled row read identically.
          text: message.text || "",
          content: message.text || "",
          created_at: new Date().toISOString(),
          files: message.files || [],
          metadata,
          contextWarning:
            metadata.contextWarning ||
            message.context_warning ||
            message.contextWarning ||
            false,
        };
        const normalized = ChatHelper.normalizeMessage(
          this.activeChatId as string,
          raw,
        );

        // Until REV-2461 ships, llm-server still puts a new letter's HTML on
        // this frame instead of announcing it with document_output_available.
        // Remove with REV-2519.
        if (message.letter_content) {
          void this.loadChatLetters(this.activeChatId);
        }

        // A turn's response arrives as a `partial` followed by a `final`/`complete`
        // (with `stream` chunks in between only when it streams). The partial was
        // only ever superseded through a `stream` message, so a partial + final
        // with no streaming left BOTH rendered — the claim analysis appeared twice
        // (REV-2067). Drop any earlier partial for this same turn (matched by
        // ref_id) before adding the new message. Scoping to ref_id preserves
        // standalone partials that carry none (e.g. the "summarized conversation"
        // notice), which are meant to persist alongside the response.
        //
        // A multi-agent turn (e.g. appeal: claim_reviewer partial, then
        // appeal_strategy final) shares one ref_id across every stage, so also
        // matching on agent_name keeps each stage's own partial from being wiped
        // out by a later stage's unrelated final — only a stage's own eventual
        // final/complete should replace its partial (REV-2298).
        const baseMessages = refId
          ? this.messages.filter(
              (m) =>
                !(
                  m.type === WEBSOCKET_MESSAGE_TYPES.partial &&
                  m.ref_id === refId &&
                  m.agent_name === message.agent_name
                ),
            )
          : this.messages;

        // A stopped turn leaves its stream bubble on screen. A later frame
        // replaces only the stream for its own ref. A ref-less frame must not
        // overwrite a stream still held for a pending stop letter.
        const ownStream = (m: IMessage) => {
          if (m.type !== WEBSOCKET_MESSAGE_TYPES.stream) return false;
          if (refId) return m.ref_id === refId || m.id === refId;
          const idx = baseMessages.indexOf(m);
          return idx >= 0 && !this.streamHeldForPendingStop(baseMessages, idx);
        };

        // A letter turn ends with a textless final after its
        // document_output_available: the letter bubble is already in place, and
        // rendering the final would add an empty "unavailable" bubble under it.
        if (
          message.type === WEBSOCKET_MESSAGE_TYPES.final &&
          this.letterTurnAwaitingFinal &&
          !message.text &&
          !message.files?.length
        ) {
          this.letterTurnAwaitingFinal = false;
          this.setMessages(
            baseMessages.map((m) =>
              ownStream(m) ? { ...m, type: "final" } : m,
            ),
          );
          this.setStatusMessages([]);
          this.setIsAborting(false);
          this.setIsQuestioning(false);
          break;
        }
        if (message.type === WEBSOCKET_MESSAGE_TYPES.final) {
          this.letterTurnAwaitingFinal = false;
        }

        // A stop frame is handled before the "no stream, so append" branch:
        // the stream bubble may already have been finalized, or the poll may
        // already have inserted this same row.
        if (message?.metadata?.aborted) {
          this.setIsAborting(false);
          this.setMessages(
            this.applyAbortFinal(
              baseMessages,
              normalized,
              Boolean(message.metadata.partial_content_length),
            ),
          );
          this.setStatusMessages([]);
        } else if (!baseMessages.some(ownStream)) {
          // This turn did not stream. Another turn's stream stays where it is.
          this.setMessages([...baseMessages, normalized]);
          // Clear the status messages
          this.setStatusMessages([]);
          this.setIsAborting(false);
        } else {
          this.setMessages(
            baseMessages.map((m) => (ownStream(m) ? normalized : m)),
          );
          this.setStatusMessages([]);
          this.setIsAborting(false);
        }

        // Partial messages may be received , follow up with stream messages
        // No need to reset isQuestioning for partial messages. agent_result is
        // the same: a finished mid-turn stage that does not end the turn
        // (REV-2298) — only the turn's actual final/complete unlocks.
        if (
          message.type !== WEBSOCKET_MESSAGE_TYPES.partial &&
          message.type !== WEBSOCKET_MESSAGE_TYPES.agent_result
        ) {
          this.setIsQuestioning(false);
        }
        break;
      }
      case WEBSOCKET_MESSAGE_TYPES.user_message: {
        // A user message this tab did not send — a turn started through the v2
        // API, or from another tab/device of the same user. Without it the
        // assistant reply would render with no prompt above it, and the turn's
        // ref_id would have nothing to anchor to (REV-2134).
        const id = message.id;
        // Deduped by server id: a history load or a message_ack for our own
        // optimistic message can already hold this row.
        if (!id || this.messages.some((m) => m.id === id)) break;
        // `type`/`chat_id` are envelope fields: dropped so the stored message is
        // shaped exactly like the same row loaded from chat history.
        const { type: _t, chat_id: _c, ...raw } = message;
        this.setMessages([
          ...this.messages,
          ChatHelper.normalizeMessage(
            this.activeChatId as string,
            {
              ...raw,
              role: "user",
            } as RawMessage,
          ),
        ]);
        break;
      }
      case WEBSOCKET_MESSAGE_TYPES.message_ack: {
        const { local_id, id } = message;
        if (local_id && id) {
          runInAction(() => {
            const idx = this.messages.findIndex((m) => m.id === local_id);
            if (idx !== -1) {
              const next = [...this.messages];
              next[idx] = { ...next[idx], id };
              this.setMessages(next);
            }
          });
        }
        break;
      }
      case WEBSOCKET_MESSAGE_TYPES.error: {
        const errorText = message.message || message.text || "Unknown error";
        console.error("[ChatStore] Error from backend:", errorText);
        // REV-2339: the turn was never dispatched and nothing is retained for
        // it, so take the optimistic bubble back out and return the text
        // rather than leaving a message that never gets a reply.
        const refused =
          message.code === "chat_busy" || message.code === "publish_failed";
        if (refused) {
          this.discardRejectedMessage(
            message.local_id,
            message.message_id,
            errorText,
          );
        }
        // A retry that did not dispatch keeps its message (REV-2553); the
        // Retry button comes back once isQuestioning clears below, unless the
        // backend says the message can no longer be retried.
        if (message.code === "retry_failed") {
          runInAction(() => {
            this.notice = errorText;
            if (message.retryable === false && this.chatDetails) {
              this.chatDetails = {
                ...this.chatDetails,
                unanswered_message_id: null,
              };
            }
          });
        }
        // `chat_busy` means another turn is still generating, so the composer
        // must stay disabled — re-enabling it just invites a second rejection.
        // Same for a retry refused because the chat is busy.
        const busy =
          message.code === "chat_busy" ||
          (message.code === "retry_failed" && message.busy === true);
        if (!busy && (!refused || message.code === "publish_failed")) {
          this.setIsQuestioning(false);
        }
        this.setIsAborting(false);
        this.setStatusMessages([]);
        break;
      }
      case WEBSOCKET_MESSAGE_TYPES.task_in_progress:
        break;
      case WEBSOCKET_MESSAGE_TYPES.status_message: {
        const details = message.details || {};
        const msg = message.message || "";
        if (!details.tool) break;

        const action = VALID_ACTIONS.includes(details.action)
          ? details.action
          : "processing";
        const phase = VALID_PHASES.includes(details.phase)
          ? details.phase
          : "complete";
        const duration =
          typeof details.duration === "number" ? details.duration : 0;
        const chat_duration =
          typeof details.chat_duration === "number" ? details.chat_duration : 0;

        runInAction(() => {
          const current = [...this.statusMessages];
          const index = current.findIndex(
            (s) =>
              s.tool === details.tool &&
              (s.phase === "start" || s.phase === "progress"),
          );
          if (
            details.phase === "start" ||
            (details.phase === "progress" && index === -1)
          ) {
            const newStatus: StatusMessageState = {
              tool: details.tool,
              action,
              message: msg,
              phase,
              duration,
              chat_duration,
              timestamp: Date.now(),
            };
            this.setStatusMessages([...current, newStatus]);
          } else if (index !== -1) {
            const updated = [...current];
            updated[index] = {
              ...updated[index],
              message: msg,
              phase,
              duration,
              chat_duration,
            };
            this.setStatusMessages(updated);

            // Auto-remove after a delay
            setTimeout(() => {
              runInAction(() => {
                const statuses = this.statusMessages;
                if (statuses) {
                  const filtered = statuses.filter(
                    (s) =>
                      !(
                        s.tool === details.tool &&
                        (s.phase === "complete" || s.phase === "failed")
                      ),
                  );
                  this.setStatusMessages(filtered);
                }
              });
            }, 3000);
          }
        });
        break;
      }
      default:
        break;
    }
  }

  // MUTATIONS
  async askQuestion(
    question: string,
    metadata?: Record<string, any>,
    agent_name?: string,
    // AskReveActionMenu (ClaimPreview): always starts a fresh chat rather than
    // continuing whatever chat happens to be active.
    forceNewChat?: boolean,
    // Persisted by the backend as an assistant message at chat creation, so it
    // shows in history and reaches the LLM as context before the first user turn.
    initialAssistantMessage?: string,
  ): Promise<string | null> {
    const allowClaimChatMode =
      forceNewChat || (!this.activeChatId && !!this.claimIdToPreview);
    if (this.isInputDisabled && !allowClaimChatMode) {
      return null;
    }

    // The LLM reads the letter from the backend at turn start: an edit still in
    // the autosave debounce would be revised away. A failed save has already
    // told the user; the question still goes out.
    if (this.letterDraft) {
      await this.saveLetterEdit();
    }

    this.setIsQuestioning(true);

    let redirectId = null;
    let message: string = question;
    let messageMetadata: Record<string, string> = {
      ...(metadata || {}),
    };

    const policyToPreview = this.mainStore?.appStore?.policyToPreview;
    const contractToPreview = this.mainStore?.appStore?.contractToPreview;
    const claimId =
      this.mainStore?.claimsStore?.claimToPreview?.claimNumber ||
      this.claimIdToPreview ||
      undefined;
    const claimNumber =
      this.mainStore?.claimsStore?.claimToPreview?.claimNumber ||
      this.claimIdToPreview ||
      undefined;
    const claimPayer = this.mainStore?.claimsStore?.claimToPreview?.payer;

    if (
      claimId &&
      this.messages?.length === 0 &&
      // Check we're on the claims page or in a draft claim chat (Ask Reve → /channels/new)
      (window.location.pathname.includes("/claims") || this.claimIdToPreview)
    ) {
      messageMetadata = { ...messageMetadata, claim_number: claimNumber || "" };
      if (claimPayer) {
        messageMetadata = { ...messageMetadata, payer: claimPayer || "" };
      }
    }

    this.setIsQuestioning(true);

    if (
      this.messages?.length === 0 &&
      (this.isPolicyQuestionsChannel || this.isContractResearchChannel) &&
      this.mainStore?.chatCategoryStore?.filters
    ) {
      // Process pages data
      if (this.isPolicyQuestionsChannel) {
        messageMetadata = {
          ...messageMetadata,
          topic: "Payer Policy Questions",
        };
      } else if (this.isContractResearchChannel) {
        messageMetadata = {
          ...messageMetadata,
          topic: "Provider Contract Search",
        };
      }

      const formData = this.mainStore?.chatCategoryStore?.filters;

      if (formData.payer) {
        const displayName = getPayerDisplayName(formData.payer);
        // Always show display name, and include API name in parentheses if different
        const payerLabel =
          displayName.toLowerCase() !== formData.payer.toLowerCase()
            ? `${displayName} (${formData.payer})`
            : displayName;
        messageMetadata = { ...messageMetadata, payer: payerLabel || "" };
      }
      if (formData.state) {
        messageMetadata = { ...messageMetadata, state: formData.state || "" };
      }
      if (formData.lineOfBusiness) {
        messageMetadata = {
          ...messageMetadata,
          line_of_business:
            LOB_VALUE_TO_NAME[formData.lineOfBusiness] ||
            formData.lineOfBusiness ||
            "",
        };
      }

      if (this.isPolicyQuestionsChannel && policyToPreview?.document_id) {
        messageMetadata = {
          ...messageMetadata,
          document_title: policyToPreview.title || "",
          document_id: policyToPreview.document_id || "",
        };
      }
      if (this.isPolicyQuestionsChannel && formData.dateOfService) {
        messageMetadata = {
          ...messageMetadata,
          date_of_service: formData.dateOfService || "",
        };
      }

      if (this.isContractResearchChannel && contractToPreview?.title) {
        messageMetadata = {
          ...messageMetadata,
          document_title: contractToPreview.title || "",
          document_id: contractToPreview.document_id || "",
        };
      }
    }

    if (
      this.previewFileChanged &&
      !this.isContractResearchChannel &&
      contractToPreview
    ) {
      messageMetadata = {
        ...messageMetadata,
        document_title: contractToPreview.title || "",
        document_id: contractToPreview.document_id || "",
      };
    }
    if (
      this.previewFileChanged &&
      !this.isPolicyQuestionsChannel &&
      policyToPreview
    ) {
      messageMetadata = {
        ...messageMetadata,
        document_title: policyToPreview.title || "",
        document_id: policyToPreview.document_id || "",
      };
    }

    // Reset the preview file changed flag
    this.setPreviewFileChanged(false);

    const formattedFiles =
      this.attachableFiles // Successfully uploaded, excluding claim-ingested EDI
        ?.map((attachedFile: any) => ({
          id: attachedFile.uploadResponse?.file_id || attachedFile.id, // Use the file_id from upload response
          name: attachedFile.name, // Use the original filename from the attached file
          size: 0, // Not available in FileUploadResponse
          url: attachedFile.url,
        })) || [];

    // Add user message to store
    const userMessage = {
      id: `local-${Date.now()}-${uuid()}`,
      channel: this.activeChatId as string,
      data: message,
      sender: "me",
      timestamp: Date.now(),
      files: formattedFiles || [],
      // Carry the document reference (and topic, which gates it) into the
      // optimistic local message so ChatStore.contractPreviewForChat (read
      // live off message metadata) picks it up immediately, before the
      // server echoes it back on reload.
      metadata: messageMetadata.document_id
        ? {
            document_id: messageMetadata.document_id,
            document_title: messageMetadata.document_title,
            topic: messageMetadata.topic,
          }
        : undefined,
    };

    const isClaimChat =
      !!this.claimIdToPreview && (!this.activeChatId || !!forceNewChat);

    try {
      // create new chat
      if (!this.activeChatId || forceNewChat) {
        const chat = await websocketService.createChatAndConnect(
          undefined,
          message,
          this.chatTag,
          formattedFiles,
          // letter_content,
          undefined,
          agent_name || this.currentChannelConfig?.agent_name,
          claimId,
          messageMetadata,
          this.experimentsForCreate,
          initialAssistantMessage,
          userMessage.id,
          { model_version: this.modelForRequest },
        );

        // Record what the server actually stored before activeChatId makes the
        // chat look loaded: otherwise the switches read as unknown until the
        // redirect finishes loading it, leaving the toggle inert and a flip in
        // that window local-only against a chat that already exists.
        this.setExperiments(chat.experiments ?? this.experimentsForCreate);
        // Seed chatDetails/messages from what we already have so ChatSection's
        // activeChatId effect (guarded by chatDetails.id there) doesn't re-fetch
        // the chat it just created and flash the pane empty mid-redirect.
        this.setChatDetails(chat as unknown as IChannel);
        userMessage.channel = chat.id;
        this.setMessages([userMessage as unknown as IMessage]);
        this.setActiveChatId(chat.id);
        // isChatLoading defaults to true (and loadChatMessages, the only other
        // place that clears it, is skipped for this chat — see the guard
        // above). Without this, a chat created from a page that never ran
        // loadChatMessages/reset() first (e.g. Home on first load) redirects
        // to /channels/:id stuck showing the loading spinner forever.
        this.setIsChatLoading(false);
        // Same skip: loadChatMessages is what normally arms lastPollTimestamp.
        // Without this, the REV-2348 owner watchdog's pollNewMessages() calls
        // would no-op and a dropped websocket would leave the spinner spinning.
        this.lastPollTimestamp = new Date().toISOString();
        redirectId = chat.id;
        if (claimId) {
          this.setClaimIdToPreview(claimId);
        }
      } else {
        // Send via WebSocket with the same local ID
        // Backend will respond with message_confirmed containing local_id + server_id
        await websocketService.sendMessage(
          this.activeChatId,
          message,
          formattedFiles,
          // letter_content,
          undefined,
          agent_name,
          userMessage.id,
          messageMetadata || {},
          { model_version: this.modelForMessage },
        );
        this.setMessages([...this.messages, userMessage as unknown as IMessage]);
      }

      if (isClaimChat) {
        this.resetAskReveal();
      }

      return redirectId;
    } catch (error) {
      this.setIsQuestioning(false);
      return null;
    }
  }

  /**
   * The backend flags an unanswered message only once it is
   * UNANSWERED_GRACE_MS old (REV-2553). A chat opened inside that window would
   * otherwise show no Retry until a manual reload: show "still sending" and
   * re-read the flag once the window has passed.
   */
  private scheduleUnansweredRecheck(chatId: string, recheckIfPast = false) {
    this.cancelUnansweredRecheck();
    if (this.chatDetails?.unanswered_message_id || this.isQuestioning) return;
    const last = this.messages[this.messages.length - 1];
    if (last?.sender !== "me" || !last.timestamp) return;
    const remaining = last.timestamp + UNANSWERED_GRACE_MS - Date.now();
    // On load, a message well past the window was already judged by the
    // server, so its payload is final. Just past it, the server may not have
    // flagged the row yet (save latency, clock skew): still re-read.
    if (!recheckIfPast && remaining <= -UNANSWERED_GRACE_MS) return;
    if (remaining > 0) {
      runInAction(() => {
        this.stillSendingMessageId = String(last.id);
      });
    }
    this.queueUnansweredRecheck(
      chatId,
      String(last.id),
      Math.max(remaining, 0) + UNANSWERED_RECHECK_SLACK_MS,
      1,
    );
  }

  private queueUnansweredRecheck(
    chatId: string,
    messageId: string,
    delayMs: number,
    attempt: number,
  ) {
    const generation = this.unansweredRecheckGeneration;
    this.unansweredRecheckTimer = setTimeout(
      () => void this.recheckUnanswered(chatId, messageId, attempt, generation),
      delayMs,
    );
  }

  private cancelUnansweredRecheck() {
    if (this.unansweredRecheckTimer) {
      clearTimeout(this.unansweredRecheckTimer);
      this.unansweredRecheckTimer = null;
    }
    this.unansweredRecheckGeneration++;
    this.stillSendingMessageId = null;
  }

  private async recheckUnanswered(
    chatId: string,
    messageId: string,
    attempt: number,
    generation: number,
  ) {
    this.unansweredRecheckTimer = null;
    runInAction(() => {
      this.stillSendingMessageId = null;
    });
    if (this.activeChatId !== chatId) return;
    const result = await chatService.getChat(chatId);
    // Cancelled or re-armed (chat left, new turn) while the request was out.
    if (generation !== this.unansweredRecheckGeneration) return;
    if (this.activeChatId !== chatId || !this.chatDetails) return;

    // The server may flag the row a moment after this read, and a failed read
    // tells us nothing: try again while our message is still the last one.
    const readAgain = () => {
      const last = this.messages[this.messages.length - 1];
      if (
        attempt < UNANSWERED_RECHECK_MAX_ATTEMPTS &&
        !this.isQuestioning &&
        last?.sender === "me" &&
        String(last.id) === messageId
      ) {
        this.queueUnansweredRecheck(
          chatId,
          messageId,
          UNANSWERED_RECHECK_SLACK_MS,
          attempt + 1,
        );
      }
    };

    if (!result.success) return readAgain();
    const id =
      (result.data as { unanswered_message_id?: string | null })
        ?.unanswered_message_id ?? null;
    runInAction(() => {
      if (this.chatDetails) {
        this.chatDetails = { ...this.chatDetails, unanswered_message_id: id };
      }
    });
    if (!id) readAgain();
  }

  async retryUnansweredMessage() {
    const chatId = this.activeChatId;
    const messageId = this.retryableMessageId;
    if (!chatId || !messageId) return;
    this.setIsQuestioning(true);
    const sent = await websocketService.sendRetry(chatId, messageId);
    if (!sent) {
      this.setIsQuestioning(false);
      runInAction(() => {
        this.notice = "Could not reach the server. Try again in a moment.";
      });
    }
  }

  async abortQuestion() {
    try {
      this.setIsAborting(true);

      // Existing chat - send message
      // Generate local ID first so we can use the same ID in store and WS
      const localId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const messageText = STOP_RESPONDING_MESSAGE;

      // Add user message to store first (with local ID)
      const userMessage: IMessage = {
        id: localId,
        channel: this.activeChatId as string,
        data: messageText,
        sender: "me",
        timestamp: Date.now(),
        metadata: { abort: true },
      };
      this.setMessages([...this.messages, userMessage as unknown as IMessage]);

      await websocketService.sendMessage(
        this.activeChatId as string,
        messageText,
        undefined, // files
        undefined, // letter_content
        undefined, // agent_name
        localId,
        { abort: true },
      );
    } catch (error) {
      console.error("[stopGenerating] Error:", error);
      this.setIsAborting(false);
    }
  }

  setDroppedFiles = async (files: FileList) => {
    const fileArray = Array.from(files);
    const newEntries: AttachedFile[] = fileArray.map((file) => ({
      id: uuid(),
      name: file.name,
      state: "uploading",
    })) as AttachedFile[];

    let currentFiles = this.uploadedFiles;

    // Do not add duplicate files
    newEntries.forEach((fileItem) => {
      if (!this.uploadedFiles?.some((file) => file.name === fileItem.name)) {
        currentFiles = [...currentFiles, fileItem];
      }
    });

    this.setUploadedFiles(currentFiles);

    // Upload one file at a time so we don't hold multiple large bodies in memory
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      const entry = newEntries[i];
      try {
        const uploadResult = await chatService.uploadFile(file);
        this.setUploadedFiles(
          this.uploadedFiles.map((f) =>
            f.id === entry.id
              ? {
                  ...f,
                  state: uploadResult.success
                    ? ("uploaded" as const)
                    : ("error" as const),
                  url: uploadResult.success
                    ? (uploadResult.data?.file_url ?? null)
                    : null,
                  uploadResponse: uploadResult.data,
                }
              : f,
          ),
        );

        const claimIngest = uploadResult.data?.claim_ingest;
        if (uploadResult.success && claimIngest?.matched) {
          const claimNumber =
            claimIngest.chat_claim_number ||
            this.claimIdToPreview ||
            (this.chatDetails as { claim_number?: string; claim_id?: string })
              ?.claim_number ||
            (this.chatDetails as { claim_number?: string; claim_id?: string })
              ?.claim_id ||
            null;
          const chatClaim =
            this.claimIdToPreview ||
            (this.chatDetails as { claim_number?: string; claim_id?: string })
              ?.claim_number ||
            (this.chatDetails as { claim_number?: string; claim_id?: string })
              ?.claim_id ||
            null;
          // Ingest succeeded — refresh claim data when this chat is already tied
          // to that claim, but never auto-open the claim panel or navigate away
          // from the chat (that hijacks focus / can drop the user on the table).
          if (claimNumber && chatClaim === claimNumber) {
            if (this.claimIdToPreview) {
              this.setPendingEdiFileNameForClaimTab(file.name);
            }
            // Debounced so a multi-file drop refetches the claim once, after
            // the last file has been ingested.
            this.scheduleClaimRefresh(claimNumber);
          }
        }
      } catch {
        this.setUploadedFiles(
          this.uploadedFiles.map((f) =>
            f.id === entry.id ? { ...f, state: "error" as const } : f,
          ),
        );
      }
    }
  };

  async setChatMessageFeedback(
    messageId: string,
    action: "up" | "down",
    isClearFeedback: boolean = false,
  ) {
    try {
      const response = await apiService.setChatMessageFeedback(
        this.activeChatId || "",
        messageId,
        action,
        isClearFeedback,
      );
      if (response.success) {
        // Update message feedback in the store
        this.setMessages(
          this.messages.map((m) =>
            m.id === messageId ? { ...m, feedback_rating: action } : m,
          ),
        );
        return true;
      }
    } catch {
      console.error("Failed to set chat message feedback");
      return false;
    }
  }

  async shareChat(): Promise<boolean> {
    const chat = this.chatToShare;
    const chatId = channelKey(chat);

    if (!chatId) {
      throw new Error("No chat selected");
    }

    const response = await apiService.updateChatShared(chatId, !chat?.shared);

    if (!response.success) {
      throw new Error(
        response.error ||
          `Failed to ${chat?.shared ? "unshare" : "share"} chat`,
      );
    }

    return true;
  }
}
